/**
 * Converts a hex color to RGB values
 * @param hex - The hex color string (with or without #)
 * @returns RGB values as an array [r, g, b]
 */
export function hexToRgb(hex: string): [number, number, number] {
  // Remove # if present
  const hexValue = hex.replace("#", "").trim();

  // Handle 3-digit hex
  const fullHex =
    hexValue.length === 3
      ? hexValue
          .split("")
          .map((c) => c + c)
          .join("")
      : hexValue;

  return [
    parseInt(fullHex.substring(0, 2), 16),
    parseInt(fullHex.substring(2, 4), 16),
    parseInt(fullHex.substring(4, 6), 16),
  ];
}

/**
 * Converts a hex color to an RGB string
 * @param hex - The hex color string (with or without #)
 * @returns RGB string in format "rgb(r, g, b)"
 */
export function hexToRgbString(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Converts a hex color to an RGBA string with custom opacity
 * @param hex - The hex color string (with or without #)
 * @param alpha - The opacity value (0-1)
 * @returns RGBA string in format "rgba(r, g, b, a)"
 */
export function hexToRgba(hex: string, alpha: number = 1): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Creates a lighter version of a hex color with custom opacity
 * @param hex - The hex color string (with or without #)
 * @param alpha - The opacity value (0-1)
 * @returns RGBA string with the lighter color
 */
export function getLightColor(hex: string, alpha: number = 0.14): string {
  if (!hex) return "rgba(216, 238, 239, 0.45)";
  return hexToRgba(hex, alpha);
}

/**
 * Darkens a hex color by a specified amount
 * @param hex - The hex color string (with or without #)
 * @param amount - The amount to darken (0-1)
 * @returns Darkened hex color string
 */
export function darkenColor(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);

  // Darken each component
  const newR = Math.max(0, Math.floor(r * (1 - amount)));
  const newG = Math.max(0, Math.floor(g * (1 - amount)));
  const newB = Math.max(0, Math.floor(b * (1 - amount)));

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, "0")}${newG
    .toString(16)
    .padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
}

/**
 * Adds opacity to an RGB color string
 * @param rgb - The RGB color string
 * @param opacity - The opacity value (0-1)
 * @returns RGBA string
 */
export function addOpacityToRgb(rgb: string, opacity: number): string {
  if (rgb.startsWith("rgb(")) {
    return rgb.replace("rgb(", "rgba(").replace(")", `, ${opacity})`);
  }
  return `rgba(0, 56, 60, ${opacity})`; // Default fallback
}
